import { Button } from "@gigpro/ui/Button";
import { Dialog, DialogFooter, DialogTitle } from "@gigpro/ui/Dialog";
import {
  getHireJobsProMatchStatusListQueryKey,
  getHireJobOffersListQueryKey,
  useHireJobOffersCreateWithTransform,
} from "@sidegigllc/api/client";
import { useQueryClient } from "@tanstack/react-query";
import { useAdminPro } from "api/queries/admin";
import { useJob, useJobsWithOfferStatus } from "api/queries/hire";
import type { JobCandidateCardData } from "domains/hire/components/JobCandidateCard";
import { SendJobOfferDialog } from "domains/hire/components/SendJobOfferDialog";
import { DirectHireDialogContainer } from "domains/hire/containers/DirectHireDialog";
import React from "react";
import { useActiveLocationId } from "services/locationOrgContext";
import { captureException } from "utils/errorReporting";
import { toastApiError } from "utils/toastApiError";
// oxlint-disable-next-line import/no-cycle -- invite, new-job, live and send-offer dialogs chain into each other by design
import { InviteToJobDialog } from "../../components/InviteToJobDialog";

interface SendHireOfferDialogContainerProps {
  open: boolean;
  onClose: () => void;
  proId: number;
  onSuccess?: () => void;
  /** Preset job: skips the job picker and goes straight to the offer step. */
  jobId?: string;
}

type AdminPro = NonNullable<ReturnType<typeof useAdminPro>["data"]>;

const toProContext = (proData: AdminPro | undefined) =>
  proData
    ? {
        name: proData.display_name ?? proData.first_name,
        imageId: proData.gigster_profile?.headshot_public_id ?? null,
      }
    : undefined;

/** Maps pro data to JobCandidateCardData. */
const toCandidateCardData = (proData: AdminPro | undefined): JobCandidateCardData | undefined =>
  proData
    ? {
        id: String(proData.id),
        first_name: proData.first_name ?? proData.display_name ?? "Pro",
        last_name: proData.last_name ?? "",
        user: proData.id,
        image_id: proData.gigster_profile?.headshot_public_id ?? null,
        star_rating: proData.rating ?? undefined,
        shifts_done: proData.gigs_worked ?? undefined,
        completion_rate:
          proData.completion_percentage != null ? Math.round(proData.completion_percentage * 100) : undefined,
      }
    : undefined;

export const SendHireOfferDialogContainer: React.FC<SendHireOfferDialogContainerProps> = ({
  open,
  onClose,
  proId,
  onSuccess,
  jobId,
}) => {
  const [pickedJobId, setPickedJobId] = React.useState<string>();
  // Undefined until the business chooses: with no jobs to pick from, hiring directly is the way in.
  const [directHireChosen, setDirectHireChosen] = React.useState<boolean>();
  const [locationId] = useActiveLocationId();
  const selectedJobId = jobId ?? pickedJobId;
  const queryClient = useQueryClient();

  const {
    jobs: jobsWithOfferStatus,
    isLoading,
    isError: jobsFailed,
    isFetching: jobsFetching,
  } = useJobsWithOfferStatus(proId, {
    enabled: open && !jobId,
  });

  // Fetch pro data for candidate card and context
  const { data: proData, isLoading: proLoading, isError: proFailed } = useAdminPro({ id: proId }, { enabled: open });

  const proContext = toProContext(proData);

  // Fetch the full job when one is selected (needed for SendJobOfferDialog)
  const { data: selectedJob, isError: jobFailed } = useJob(selectedJobId ?? "", { enabled: !!selectedJobId });

  const candidateCardData = toCandidateCardData(proData);

  const createOfferMutation = useHireJobOffersCreateWithTransform();

  const handleJobSelect = (jobIds: Array<string>) => {
    if (jobIds.length > 0) {
      setPickedJobId(jobIds[0]);
    }
  };

  const handleSendOffer = async (deadline: Date) => {
    if (!selectedJobId) return;
    try {
      await createOfferMutation.mutateAsync({
        data: {
          pro_id: proId,
          job_posting_id: selectedJobId,
          expires_at: deadline,
        },
      });
      await Promise.all([
        queryClient.refetchQueries({ queryKey: getHireJobsProMatchStatusListQueryKey() }),
        queryClient.refetchQueries({ queryKey: getHireJobOffersListQueryKey() }),
      ]);
      onSuccess?.();
    } catch (error) {
      toastApiError(error);
      captureException(error);
      throw error; // re-throw so SendJobOfferDialog doesn't show success
    }
  };

  const handleClose = () => {
    setPickedJobId(undefined);
    setDirectHireChosen(undefined);
    onClose();
  };

  const handleOfferDialogClose = () => {
    setPickedJobId(undefined);
    onClose();
  };

  // Step 2: Show SendJobOfferDialog when a job is selected
  if (open && selectedJobId && selectedJob && candidateCardData) {
    return (
      <SendJobOfferDialog
        open={open}
        onClose={handleOfferDialogClose}
        candidate={candidateCardData}
        job={selectedJob}
        onSendOffer={handleSendOffer}
        isPending={createOfferMutation.isPending}
        proId={proId}
      />
    );
  }

  if (jobId) {
    // Preset job whose job or pro failed to load: say so instead of a blank screen
    if (open && (jobFailed || proFailed)) {
      return (
        <Dialog open onClose={onClose} className="max-w-md" bottomSheet closeOnBackdropClick>
          <DialogTitle>Can't send offer</DialogTitle>
          <p>We couldn't load this job or pro. Please try again.</p>
          <DialogFooter>
            <Button onClick={onClose}>Close</Button>
          </DialogFooter>
        </Dialog>
      );
    }
    // Never flash the picker while the job and pro load, or after close
    return null;
  }

  // Only fresh, successful data counts: a failed fetch or a cached empty list says nothing about the jobs
  const hasNoJobs = open && !isLoading && !jobsFetching && !jobsFailed && jobsWithOfferStatus.length === 0;
  // Latched: sending the offer creates a private job, and the refetch that follows must not swap the form for the picker.
  if (directHireChosen === undefined && hasNoJobs) setDirectHireChosen(true);
  if (open && locationId && proContext && directHireChosen) {
    return (
      <DirectHireDialogContainer
        open={open}
        onClose={handleClose}
        locationId={locationId}
        proId={proId}
        proFirstName={proContext.name}
        onSuccess={onSuccess}
        onPostJobInstead={() => setDirectHireChosen(false)}
      />
    );
  }

  // Step 1: Job picker
  return (
    <InviteToJobDialog
      open={open}
      onClose={handleClose}
      jobs={jobsWithOfferStatus}
      isLoading={isLoading}
      isSubmitting={false}
      onConfirm={handleJobSelect}
      proContext={proContext}
      variant="hire"
      onHireDirectly={locationId ? () => setDirectHireChosen(true) : undefined}
      proLoading={proLoading}
      proFailed={proFailed && !proData}
    />
  );
};
